import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const statusMeta = {
  on_track: { label: "On Track", dot: "bg-green-600", text: "text-green-700" },
  off_track: { label: "Off Track", dot: "bg-red-600", text: "text-red-700" },
  done: { label: "Done", dot: "bg-neutral-500", text: "text-neutral-600" },
};

function defaultQuarter() {
  const d = new Date(); const q = Math.floor(d.getMonth() / 3) + 1;
  return `Q${q} ${d.getFullYear()}`;
}

export default function Rocks() {
  const [rocks, setRocks] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", owner: "", quarter: defaultQuarter(), status: "on_track", due_date: "", notes: "" });

  const load = () => api.get("/rocks").then((r) => setRocks(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title.trim()) return;
    await api.post("/rocks", form);
    setOpen(false); setForm({ title: "", owner: "", quarter: defaultQuarter(), status: "on_track", due_date: "", notes: "" });
    toast.success("Rock added.");
    load();
  };

  const updateStatus = async (r, status) => {
    await api.put(`/rocks/${r.id}`, { ...r, status });
    load();
  };

  const remove = async (id) => { if (confirm("Delete this rock?")) { await api.delete(`/rocks/${id}`); load(); } };

  const byQuarter = rocks.reduce((acc, r) => { (acc[r.quarter] ??= []).push(r); return acc; }, {});

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-start justify-between">
        <div>
          <div className="mono text-xs uppercase tracking-widest text-neutral-500">Module · Rocks</div>
          <h1 className="font-display text-4xl font-bold mt-2">Quarterly Rocks</h1>
          <p className="text-neutral-600 mt-2">The 3–7 priorities that will make this quarter matter.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#E54D2E] hover:bg-[#C93E22] text-white rounded-sm btn-lift" data-testid="rocks-add-btn"><Plus className="h-4 w-4 mr-1" /> Add Rock</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New Rock</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Title</Label><Input value={form.title} onChange={(e)=>setForm({...form,title:e.target.value})} className="rounded-sm" data-testid="rock-title-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Owner</Label><Input value={form.owner} onChange={(e)=>setForm({...form,owner:e.target.value})} className="rounded-sm" data-testid="rock-owner-input" /></div>
                <div><Label>Quarter</Label><Input value={form.quarter} onChange={(e)=>setForm({...form,quarter:e.target.value})} className="rounded-sm" data-testid="rock-quarter-input" /></div>
              </div>
              <div><Label>Due date</Label><Input type="date" value={form.due_date} onChange={(e)=>setForm({...form,due_date:e.target.value})} className="rounded-sm" data-testid="rock-due-input" /></div>
              <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e)=>setForm({...form,notes:e.target.value})} className="rounded-sm" data-testid="rock-notes-input" /></div>
              <Button onClick={create} className="w-full bg-neutral-900 hover:bg-neutral-800 rounded-sm" data-testid="rock-create-submit">Create Rock</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-8 space-y-8">
        {Object.keys(byQuarter).length === 0 && (
          <div className="border border-neutral-200 bg-white p-12 text-center text-neutral-500">No rocks yet. Add your first priority →</div>
        )}
        {Object.entries(byQuarter).map(([q, list]) => (
          <div key={q}>
            <div className="mono text-xs uppercase text-neutral-500 tracking-widest mb-3">{q}</div>
            <div className="border border-neutral-200 bg-white">
              {list.map((r) => {
                const s = statusMeta[r.status];
                return (
                  <div key={r.id} className="border-b border-neutral-100 last:border-b-0 p-4 flex items-center gap-4 hover:bg-neutral-50" data-testid={`rock-row-${r.id}`}>
                    <span className={`w-2 h-2 ${s.dot}`} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{r.title}</div>
                      <div className="text-xs text-neutral-500 mt-0.5">{r.owner || "Unassigned"}{r.due_date ? ` · Due ${r.due_date}` : ""}</div>
                    </div>
                    <Select value={r.status} onValueChange={(v)=>updateStatus(r,v)}>
                      <SelectTrigger className={`rounded-sm w-36 ${s.text}`} data-testid={`rock-status-${r.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="on_track">On Track</SelectItem>
                        <SelectItem value="off_track">Off Track</SelectItem>
                        <SelectItem value="done">Done</SelectItem>
                      </SelectContent>
                    </Select>
                    <button onClick={() => remove(r.id)} className="text-neutral-400 hover:text-red-600" data-testid={`rock-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
